import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Users, ChevronLeft, RefreshCw, Loader2, AlertCircle, Check, Trash2, Search, Plus, X,
  Lock, FileSpreadsheet, Printer, Hash, GraduationCap, CalendarDays, ShieldAlert,
  ArrowRightLeft,
} from "lucide-react";
import {
  useAdminUpgradeReviewDietQuery,
  useAdminAddUpgradeReviewDietApplicationsMutation,
  useAdminRemoveUpgradeReviewDietApplicationMutation,
  useAdminAssignCommitteeBulkMutation,
  useAdminUnassignCommitteeMutation,
  useAdminUpdateUpgradeReviewDietMutation,
} from "../api/upgradeApi";
import { exportUpgradesPdf, exportUpgradesSpreadsheet } from "../utils/exportUpgrades";
import { isFullyEndorsed } from "../utils/upgradeStatus";
import IssueMemberNumbersModal from "../components/IssueMemberNumbersModal";
import ApplyGradeUpgradeModal from "../components/ApplyGradeUpgradeModal";
import RecordCommitteeDecisionModal from "../components/RecordCommitteeDecisionModal";
import OverrideCommitteeDecisionModal from "../components/OverrideCommitteeDecisionModal";
import DietLeviesCard from "../components/DietLeviesCard";
import RecommendedApplicantsCard from "../components/RecommendedApplicantsCard";
import ReassignCommitteeReviewModal from "../components/ReassignCommitteeReviewModal";
import CommitteePanelMemberModal from "../components/CommitteePanelMemberModal";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

/**
 * One upgrade review cycle: the panel sitting on it, the applications it is
 * deciding, and the pool still waiting to be pulled in.
 *
 * Mirrors /admin/logbook-assessment/{id} — seat the panel, pull work into the
 * cycle, hand it out.
 */

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

/**
 * A reviewer chip's colour, by the decision on that reviewer's row.
 *
 * Keyed by BOTH vocabularies on purpose. A committee review row carries
 * approved / rejected / pending (upgrade_committee_reviews.decision); the
 * application it moves carries recommended / not_recommended. The chips are
 * review rows, so without the first pair every decided chip fell through to
 * the pending amber and read as "has not decided yet" — which is the one thing
 * the secretariat is looking at this column to tell.
 */
const DECISION_STYLE: Record<string, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
  recommended: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_recommended: "bg-rose-50 text-rose-700 border-rose-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
};

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  ongoing: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-gray-100 text-gray-600 border-gray-200",
};

/**
 * Which slice of the cycle's applications is on screen — and in the export.
 *
 * A cycle runs to hundreds of rows, and "what did we recommend" is a question
 * you answer by looking at the recommended ones, not by reading a status column
 * down the page.
 *
 * The slices PARTITION the cycle: every application has exactly one status, and
 * the only one that splits is `under_review`, by whether anybody has been
 * handed it. So the chip counts add up to the cycle's own total, which is what
 * makes them readable as a report of where the sitting stands.
 */
type DecisionFilter =
  | "all"
  | "unassigned"
  | "awaiting"
  | "recommended"
  | "not_recommended"
  | "approved"
  | "rejected"
  | "other";

const FILTER_LABEL: Record<DecisionFilter, string> = {
  all: "All",
  unassigned: "Not yet assigned",
  awaiting: "Awaiting a decision",
  recommended: "Recommended",
  not_recommended: "Not recommended",
  approved: "Approved",
  rejected: "Rejected",
  other: "Other statuses",
};

/** Which slice a row belongs to. Exactly one, by construction — see above. */
function sliceOf(row: any): DecisionFilter {
  const status = String(row?.status ?? "").toLowerCase();

  if (status === "under_review") {
    return (row?.reviews?.length ?? 0) === 0 ? "unassigned" : "awaiting";
  }

  return (["recommended", "not_recommended", "approved", "rejected"] as const).includes(
    status as any,
  )
    ? (status as DecisionFilter)
    : "other";
}

/**
 * A filter chip carrying its own count.
 *
 * Chips rather than a select: the counts are half the answer, and a select
 * hides them until it is opened.
 */
function Chip({
  label,
  count,
  on,
  onClick,
  title,
}: {
  label: string;
  count: number;
  on: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-bold transition ${
        on
          ? "bg-gray-900 text-white border-gray-900"
          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
      }`}
    >
      {label}
      <span
        className={`tabular-nums text-[10px] font-black px-1.5 py-0.5 rounded-full ${
          on ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Which column on `members` keeps the number held AT each grade.
 *
 * The same map the server works from (UpgradeGradeController). Read in both
 * directions: the TO grade says where the number issued for the upgrade sits
 * while it waits, the FROM grade says where the old number is filed once the
 * grade is conferred. Fellow is absent because the Institute issues no
 * fellowship series — a Fellow keeps the number they came in with.
 */
const GRADE_NUMBER_COLUMN: Record<string, string> = {
  student: "student_member_no",
  probationer: "prob_member_no",
  member: "member_member_no",
};

/**
 * The number this row is sorted by: the one issued for the grade being
 * conferred — the "temporary" number, until the upgrade makes it the real one.
 *
 * Returned as a number, not a string. The series is zero-padded, so a text
 * sort would scatter 6970 away from 06970 the moment one record was stored
 * without its leading zero. Anything non-numeric in the value is ignored for
 * the same reason: it is the position in the series that orders the roll.
 *
 * A Fellow has no series of their own, so for them the number in force is the
 * only one there is — sorting a fellowship cycle by name instead would be an
 * accident of there being no second number, not a decision.
 */
function issuedNumber(row: any): number | null {
  const member = row?.user?.member;

  if (!member) return null;

  const col = GRADE_NUMBER_COLUMN[String(row?.to_grade ?? "").trim().toLowerCase()];
  const raw = String((col ? member[col] : member.membership_no) ?? "").replace(/\D/g, "");

  return raw === "" ? null : Number(raw);
}

/**
 * Why this applicant would be passed over by the Upgrade button, or null.
 *
 * The server's own order of tests, applied to the row the screen already has
 * (UpgradeGradeController::batch). Kept in step with it deliberately: a count
 * that is four short of the cycle, with nothing on any row saying which four,
 * is a puzzle the secretariat has to solve by opening applications one at a
 * time.
 *
 * Says nothing about a row that HAS been conferred — that is not a blocker,
 * it is the work already done, and the Numbers cell shows it as such.
 */
function conferBlocker(row: any): string | null {
  const member = row?.user?.member;

  if (!member) return "no member record";

  const toGrade = String(row?.to_grade ?? "").trim().toLowerCase();
  const col = GRADE_NUMBER_COLUMN[toGrade];

  // Already carried up. Not a blocker.
  if (row?.to_category?.id && String(member.grade ?? "") === String(row.to_category.id)) {
    return null;
  }

  const status = String(row?.status ?? "").toLowerCase();

  if (status !== "approved") {
    return `not approved — ${row?.status || "no status"}`;
  }

  if (col && !member[col]) return "no number issued yet";

  if (!row?.from_category?.id) return "no from-grade on the application";

  if (String(member.grade ?? "") !== String(row.from_category.id)) {
    return "grade has changed since they applied";
  }

  return null;
}

/**
 * The three numbers a row can be carrying, and which of them is in force.
 *
 * An upgrade has two halves, and between them a member holds two numbers at
 * once: `membership_no` is still the old one, and the number issued for the
 * new grade is sitting in that grade's column waiting to take over. The
 * secretariat has to be able to see both, and to tell at a glance which stage
 * each person is at — that is the whole of the numbering-then-conferring
 * sequence, read off one column.
 */
function MemberNumbers({ row }: { row: any }) {
  const member = row?.user?.member;

  const toGrade = String(row?.to_grade ?? "").trim().toLowerCase();
  const fromGrade = String(row?.from_grade ?? "").trim().toLowerCase();

  const current: string = member?.membership_no || "";
  const issued: string = GRADE_NUMBER_COLUMN[toGrade]
    ? member?.[GRADE_NUMBER_COLUMN[toGrade]] || ""
    : "";
  const previous: string = GRADE_NUMBER_COLUMN[fromGrade]
    ? member?.[GRADE_NUMBER_COLUMN[fromGrade]] || ""
    : "";

  // Conferred when the member's grade has actually moved to the target — the
  // same test the Upgrade button uses, so the two cannot disagree about who
  // has been carried up.
  const upgraded =
    !!member && !!row?.to_category?.id && String(member.grade ?? "") === String(row.to_category.id);

  const blocker = conferBlocker(row);

  if (!member) {
    return (
      <span className="text-[11px] text-amber-700 font-semibold">
        No member record — cannot be upgraded
      </span>
    );
  }

  return (
    <div className="space-y-0.5 text-[11px] leading-relaxed">
      <div>
        <span className="text-gray-400">In force </span>
        <span className="font-mono font-bold text-gray-900">{current || "—"}</span>
      </div>

      {issued && !upgraded ? (
        <div>
          <span className="text-gray-400">Issued </span>
          <span className="font-mono font-bold text-indigo-700">{issued}</span>
          {/* Named "temporary" because that is what the secretariat calls it:
              allocated, but not yet the number the member goes by. */}
          <span className="ml-1 text-amber-600 font-semibold">temporary</span>
        </div>
      ) : null}

      {upgraded ? (
        <>
          <div className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
            <Check className="w-3 h-3" />
            Upgraded
          </div>
          {previous ? (
            <div>
              <span className="text-gray-400">Was </span>
              <span className="font-mono text-gray-500 line-through">{previous}</span>
              <span className="ml-1 text-gray-400">kept as {row.from_grade} no.</span>
            </div>
          ) : null}
        </>
      ) : !issued ? (
        <div className="text-gray-400">No number issued yet</div>
      ) : null}

      {/* What stands between this row and the Upgrade button, on the row
          itself. Without it the only symptom is a badge that counts fewer
          than the cycle holds. */}
      {blocker ? (
        <div className="inline-flex items-start gap-1 text-amber-700 font-semibold">
          <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
          <span>Will be skipped — {blocker}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The date this cycle's upgrades take effect from.
 *
 * It is written into members.<grade>_date when the grade is conferred, which
 * is what a certificate is dated from and what seniority is read off — so it
 * is set by hand, once, for the whole sitting, and the Upgrade button refuses
 * until it is. Nothing is defaulted in: today's date would be the day of the
 * data entry, and a plausible wrong date here is never noticed.
 *
 * Editable on a CLOSED cycle, deliberately, and not tied to `canAct`. What
 * closing a cycle locks is the reviewing; conferring the decisions it reached
 * comes afterwards, and by then the cycle is usually closed.
 *
 * Behind the same permission as conferring the grade, since that is the only
 * act that reads it.
 */
function ConferredOnField({ diet }: { diet: any }) {
  const [updateDiet, { isLoading }] = useAdminUpdateUpgradeReviewDietMutation();

  const saved: string = String(diet?.conferred_on ?? "").slice(0, 10);
  const [value, setValue] = useState(saved);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  // The cycle is refetched after saving, so the row can arrive with a date
  // typed by somebody else. Follow it unless this admin is mid-edit.
  const [touched, setTouched] = useState(false);
  const shown = touched ? value : saved;

  const today = new Date().toISOString().slice(0, 10);

  const save = async (next: string) => {
    setErr(null);
    setOk(false);
    try {
      const res: any = await updateDiet({
        id: diet.id,
        // The endpoint validates a title on every update, so it travels along
        // unchanged rather than being made optional for this one field.
        title: diet.title,
        conferred_on: next || null,
      }).unwrap();

      // Read the date back off the saved record before believing it.
      //
      // An API that predates the conferred_on column answers 200 and drops the
      // field on the floor — the request is valid, there is simply nothing to
      // write it to. Without this the input would just spring back empty and
      // look like the field "refuses to set", with nothing anywhere saying why.
      const stored = String(res?.data?.conferred_on ?? "").slice(0, 10);

      if ((next || "") !== stored) {
        setErr(
          "The server accepted this but did not store the date — this API has not had the " +
            "conferred_on migration run on it yet.",
        );
        return;
      }

      setTouched(false);
      setOk(true);
    } catch (e: any) {
      setErr(String(e?.data?.message || e?.message || "Could not save the date."));
    }
  };

  return (
    <AccessGate perm={PERMS.upgrade.memberNumberIssue}>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider ${
            saved ? "text-gray-400" : "text-amber-600"
          }`}
        >
          <CalendarDays className="w-3.5 h-3.5" />
          Date conferred
        </span>
        <input
          type="date"
          value={shown}
          max={today}
          disabled={isLoading}
          onChange={(e) => {
            const next = e.target.value;
            setTouched(true);
            setValue(next);
            setOk(false);
            // Saved as soon as a whole date is picked, not on blur. A date
            // picker is usually dismissed by clicking the very button this
            // date has to be set before — which is a blur that arrives too
            // late, if the browser sends one at all. A partial date (the
            // browser reports "") is left alone until blur.
            if (next.length === 10) save(next);
          }}
          onBlur={() => {
            if (touched && value !== saved) save(value);
          }}
          className={`px-2.5 py-1 rounded-lg border text-xs font-semibold outline-none focus:ring-2 focus:ring-emerald-100 ${
            saved ? "border-gray-200 text-gray-700" : "border-amber-300 bg-amber-50 text-amber-800"
          }`}
        />
        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" /> : null}
        {ok && !isLoading ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
            <Check className="w-3.5 h-3.5" />
            Saved
          </span>
        ) : null}
        {!saved && !isLoading && !err ? (
          <span className="text-[11px] font-semibold text-amber-600">
            Needed before anyone can be upgraded
          </span>
        ) : null}
        {err ? (
          <span className="text-[11px] font-semibold text-rose-600 max-w-md">{err}</span>
        ) : null}
      </div>
    </AccessGate>
  );
}

function Stat({ label, value, tone }: { label: string; value: any; tone?: string }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-4">
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</div>
      <div className={`mt-1 text-2xl font-black tabular-nums ${tone ?? "text-gray-900"}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

/** A `{ label: count }` map, biggest first, as a compact bar list. */
function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const rows = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  if (!rows.length) return null;
  const max = rows[0][1] || 1;

  return (
    <div>
      <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">{title}</h3>
      <div className="space-y-1.5">
        {rows.map(([label, count]) => (
          <div key={label} className="flex items-center gap-3">
            <div className="w-40 shrink-0 truncate text-xs font-semibold text-gray-600">{label}</div>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-indigo-400"
                style={{ width: `${Math.max(4, (count / max) * 100)}%` }}
              />
            </div>
            <div className="w-8 text-right text-xs font-bold tabular-nums text-gray-700">{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminUpgradeReviewDietDetailsPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  /* The whole row opens the application on the two read-only tables — the pool
     and the held-back list. The name link stays: it is what makes the row
     obviously openable, and it is what a middle-click or "open in new tab"
     needs.

     Deliberately NOT on the in-cycle table, where every other cell is already
     interactive — a checkbox, a reviewer chip, an override and a remove. A
     misfire there would navigate away from a half-made selection. */
  const openApplication = (applicationId: string | number) =>
    navigate(`/admin/upgrade-applications/${applicationId}`);
  const { data, isLoading, isFetching, isError, refetch } = useAdminUpgradeReviewDietQuery(id, {
    skip: !id,
  });

  const [addApps, { isLoading: adding }] = useAdminAddUpgradeReviewDietApplicationsMutation();
  const [removeApp] = useAdminRemoveUpgradeReviewDietApplicationMutation();
  const [assignBulk, { isLoading: assigning }] = useAdminAssignCommitteeBulkMutation();
  const [unassign, { isLoading: unassigning }] = useAdminUnassignCommitteeMutation();

  const diet = data?.diet;
  // The panel IS the Membership Committee — nobody is seated or removed.
  const panel: any[] = useMemo(() => data?.members ?? [], [data]);

  /* ── Grade filter ─────────────────────────────────────────────────────────
     Carried in from the category page, through the cycles list. A cycle holds
     every grade, so this narrows the three application tables to the grade the
     admin came here to work on — the panel, the analysis and the counts stay
     whole, because those describe the sitting itself and would be a lie if they
     only counted part of it. */
  const [params] = useSearchParams();
  const categoryId = params.get("category") ?? "";
  const grade = params.get("grade") ?? "";
  const filtering = !!categoryId && !!grade;

  const ofGrade = useMemo(() => {
    const wanted = grade.trim().toLowerCase();
    return (list: any[]) =>
      filtering ? list.filter((a) => String(a?.to_grade ?? "").trim().toLowerCase() === wanted) : list;
  }, [filtering, grade]);

  const allInDiet: any[] = useMemo(() => data?.in_diet ?? [], [data]);
  const allUnassigned: any[] = useMemo(() => data?.unassigned ?? [], [data]);
  const allNotReady: any[] = useMemo(() => data?.not_ready ?? [], [data]);

  const inDiet: any[] = useMemo(() => ofGrade(allInDiet), [ofGrade, allInDiet]);
  const unassignedPool: any[] = useMemo(() => ofGrade(allUnassigned), [ofGrade, allUnassigned]);
  const notReady: any[] = useMemo(() => ofGrade(allNotReady), [ofGrade, allNotReady]);

  const analysis = data?.analysis;

  /* ── Is this cycle still the one sitting? ─────────────────────────────────
     Only the ongoing cycle takes work. A closed one is the record of what it
     decided; one that has not been started is not sitting yet, and since only
     one cycle may sit at a time, "ongoing" is never ambiguous about which.

     The server says so (`can_act`) rather than the screen working it out, so
     the buttons and the rule they stand for cannot drift apart; the local
     reading is only the fallback for an older API that sends no flag. */
  const canAct = data?.can_act ?? (diet?.status === "ongoing" || !!diet?.is_active);

  /* What is locked is the REVIEWING — adding work, handing it out, recording
     decisions. Carrying those decisions out is not: numbering, the date the
     grade is conferred from and the upgrade itself all run on a closed cycle,
     and normally that is exactly when they are run, because the sitting has
     finished deciding. So this reason is worded for the buttons it actually
     greys out and does not claim the cycle is untouchable. */
  const lockedReason =
    diet?.status === "closed"
      ? "This cycle is closed. Applications can no longer be added to it, handed out or decided in it."
      : "This cycle has not been started. Start it from the review cycles list before adding applications or handing them out.";

  /* ── Numbering, from the sitting that endorsed them ──────────────────────
     Offered here as well as on the category screen, because this is where the
     secretariat actually works a sitting through.

     Two grades carry a series, and they are different series written to
     different columns — Probationer to prob_member_no, Member to
     member_member_no. A cycle holds every grade, so the batch is scoped to
     THIS cycle AND one grade: a Fellow sitting in the same cycle must never be
     handed either number.

     Which grade is read off the rows rather than off the URL filter, so the
     button behaves the same whether or not the admin arrived through a grade.
     Where both are in one cycle the grade filter chooses; without one, the
     grade the cycle holds most of. */
  const numbering = useMemo(() => {
    const SERIES: Record<string, string> = {
      probationer: "prob_member_no",
      member: "member_member_no",
    };

    const counts: Record<string, any[]> = {};
    for (const a of allInDiet) {
      const g = String(a?.to_grade ?? "").trim().toLowerCase();
      if (!SERIES[g]) continue;
      counts[g] = [...(counts[g] ?? []), a];
    }

    const wanted = String(grade).trim().toLowerCase();
    const chosen = SERIES[wanted]
      ? wanted
      : Object.keys(counts).sort((a, b) => counts[b].length - counts[a].length)[0];

    if (!chosen || !counts[chosen]?.length) return null;

    const rows = counts[chosen];

    const col = SERIES[chosen];
    const isApproved = (a: any) => String(a?.status).toLowerCase() === "approved";
    const hasNumber = (a: any) => !!a.user?.member?.[col];
    const atFromGrade = (a: any) =>
      !!a.from_category?.id && String(a.user?.member?.grade ?? "") === String(a.from_category.id);

    /* Why the badge is smaller than the cycle.
       Each applicant is counted against the FIRST test they fail, in the order
       the server applies them, so the reasons add up to the shortfall exactly
       rather than double-counting somebody who fails two. */
    const approvedRows = rows.filter(isApproved);
    const numberedRows = approvedRows.filter(hasNumber);

    const shortfall = {
      other_grade: allInDiet.length - rows.length,
      not_approved: rows.length - approvedRows.length,
      no_member_record: approvedRows.filter((a) => !a.user?.member).length,
      no_number: approvedRows.filter((a) => !!a.user?.member && !hasNumber(a)).length,
      grade_moved: numberedRows.filter((a) => !atFromGrade(a)).length,
    };

    return {
      grade: chosen,
      label: chosen.charAt(0).toUpperCase() + chosen.slice(1),
      categoryId: rows[0]?.to_category?.id ?? null,
      inCycle: allInDiet.length,
      ofGrade: rows.length,
      shortfall,
      // What pressing it would do, counted from the rows already on screen. The
      // server decides for real, and the dialog previews that before anything
      // is written.
      awaiting: rows.filter((a) => isFullyEndorsed(a) && !a.user?.member?.[SERIES[chosen]]).length,
      // Ready to be CONFERRED: approved, already carrying the number this
      // series issued, and STILL the grade they applied from. Counted
      // separately from `awaiting` because they are two different acts on two
      // different sets — numbering comes first, and the same applicant appears
      // in one count and then the other.
      //
      // The from-grade test is what stops the badge counting people the button
      // has already carried up; it mirrors the server's own rule, which is the
      // one that actually decides (UpgradeGradeController).
      upgradable: numberedRows.filter(atFromGrade).length,
    };
  }, [allInDiet, grade]);

  /** The badge's own arithmetic, in words, for the button's tooltip. */
  const upgradeHint = useMemo(() => {
    if (!numbering) return undefined;

    const s = numbering.shortfall;
    const why = [
      [s.other_grade, "not upgrading to this grade"],
      [s.not_approved, "not approved"],
      [s.no_member_record, "no member record"],
      [s.no_number, "no number issued yet"],
      [s.grade_moved, "already moved grade"],
    ]
      .filter(([n]) => (n as number) > 0)
      .map(([n, text]) => `${n} ${text}`)
      .join(" · ");

    return (
      `${numbering.upgradable} of the ${numbering.inCycle} application(s) in this cycle can be ` +
      `conferred the ${numbering.label} grade` +
      (why ? `. Passed over: ${why}.` : ".")
    );
  }, [numbering]);

  const [showIssueNumbers, setShowIssueNumbers] = useState(false);
  const [showUpgradeGrade, setShowUpgradeGrade] = useState(false);

  /* The assignment row whose recommendation the secretariat is keying for the
     committee member it belongs to. Holds the row AND its application, because
     the dialog names both — "on behalf of X" on "Y's application". */
  const [deciding, setDeciding] = useState<{ app: any; review: any } | null>(null);

  /* The application the secretariat is deciding on the committee's behalf —
     the override. Separate from `deciding` above because they are two different
     acts: that one is one member's recommendation, keyed for them; this one is
     the committee stage decided without a recommendation at all. */
  const [overriding, setOverriding] = useState<any | null>(null);

  /* The panel member whose work is being looked at. The whole row set is
     already on this page — the dialog is a lens over it, not another fetch. */
  const [panelMember, setPanelMember] = useState<any | null>(null);

  /* The assignment being moved to somebody else. Held as the application AND
     the review row, because the dialog names both and the move acts on the one
     row belonging to the member who is giving it up. */
  const [movingReview, setMovingReview] = useState<{ app: any; review: any } | null>(null);

  const [pulled, setPulled] = useState<(string | number)[]>([]);
  const [picked, setPicked] = useState<(string | number)[]>([]);
  const [reviewer, setReviewer] = useState("");
  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);

  /* How many of the rows on screen the Upgrade button would pass over, and a
     way to see only those. Counted off `inDiet` rather than `filtered` so the
     chip does not change as the admin searches — it is a property of the
     cycle, not of the search. */
  const blockedCount = useMemo(() => inDiet.filter((a) => !!conferBlocker(a)).length, [inDiet]);
  const [onlyBlocked, setOnlyBlocked] = useState(false);

  /* ── Decision and grade chips ────────────────────────────────────────────
     Narrowing the table by where each application has got to, and — on a cycle
     being worked whole — by the grade being applied for. The two stack: "the
     Fellows we have not decided yet" is a question somebody asks of a sitting,
     and it is two chips rather than a column read down the page. */
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("all");
  const [gradeFilter, setGradeFilter] = useState("all");

  /** Each slice's count, off the whole grade-scoped cycle — see sliceOf(). */
  const sliceCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of inDiet) {
      const slice = sliceOf(a);
      counts[slice] = (counts[slice] ?? 0) + 1;
    }
    return counts;
  }, [inDiet]);

  const byDecision = useMemo(
    () => (decisionFilter === "all" ? inDiet : inDiet.filter((a) => sliceOf(a) === decisionFilter)),
    [inDiet, decisionFilter],
  );

  /* Grade chips, counted off the DECISION-filtered rows, so they answer "how do
     the recommended break down" rather than repeating the cycle's totals under
     every slice.

     A grade can stay selected while the decision filter changes under it — Member
     is picked, then Rejected, and no Member was rejected. Its chip is kept on
     screen at 0 rather than leaving an empty table with nothing highlighted to
     explain it. */
  const gradeChips = useMemo(() => {
    const counts: Record<string, number> = {};

    for (const a of byDecision) {
      const g = String(a.to_grade ?? "").trim() || "Unknown";
      counts[g] = (counts[g] ?? 0) + 1;
    }

    if (gradeFilter !== "all" && counts[gradeFilter] === undefined) counts[gradeFilter] = 0;

    return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [byDecision, gradeFilter]);

  const chipped = useMemo(
    () =>
      gradeFilter === "all"
        ? byDecision
        : byDecision.filter((a) => (String(a.to_grade ?? "").trim() || "Unknown") === gradeFilter),
    [byDecision, gradeFilter],
  );

  const chipsOn = decisionFilter !== "all" || gradeFilter !== "all";

  /* The server's by_decision, read in words. It keys by the status as stored so
     that it and the chips above name the same thing; the prettifying is the
     screen's job, and an older API that sends no by_decision falls back to the
     slices this page counts for itself — the same numbers either way. */
  const decisionBreakdown = useMemo(() => {
    const raw: Record<string, number> = analysis?.by_decision ?? {};
    // The fallback is the chips' own partition, which splits under_review into
    // assigned and not — a finer cut of the same total, never a different one.
    const source = Object.keys(raw).length ? raw : sliceCounts;

    const out: Record<string, number> = {};
    for (const [key, count] of Object.entries(source)) {
      const label =
        FILTER_LABEL[key as DecisionFilter] ?? String(key).replace(/_/g, " ");
      out[label] = (out[label] ?? 0) + Number(count ?? 0);
    }
    return out;
  }, [analysis, sliceCounts]);

  const clearChips = () => {
    setDecisionFilter("all");
    setGradeFilter("all");
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const searched = !s
      ? chipped
      : chipped.filter((a) =>
          `${a.name ?? ""} ${a.membership_no ?? ""} ${a.to_grade ?? ""}`.toLowerCase().includes(s),
        );

    const rows = onlyBlocked ? searched.filter((a) => !!conferBlocker(a)) : searched;

    /* Ordered by the number issued for the new grade.
       That is the order the numbers were handed out in, so the serial column
       runs alongside the series and a printed roll can be checked against the
       register a line at a time. Compared as numbers, not text: the series is
       zero-padded, and "6970" must not sort away from "06970".
       Anyone not yet numbered has nothing to sort by and goes last, by name,
       where they read as the outstanding work they are. */
    return [...rows].sort((a, b) => {
      const na = issuedNumber(a);
      const nb = issuedNumber(b);

      if (na === null && nb === null) return String(a.name ?? "").localeCompare(String(b.name ?? ""));
      if (na === null) return 1;
      if (nb === null) return -1;

      return na - nb;
    });
  }, [chipped, q, onlyBlocked]);

  /* Exports carry what is on screen, filed the same way, and say so: the
     sitting they were taken from, the grade filter if one is on, and the search
     if one was typed. A listing with no statement of what it is a listing of is
     worthless a week later. */
  const exportMeta = {
    // The grade the listing covers: the one carried in from the URL, or — on a
    // cycle being worked whole — the grade chip that is up. Without this a
    // chipped-down roll printed as though it covered every grade in the sitting.
    category: grade || (gradeFilter === "all" ? "" : gradeFilter),
    tab: "all" as const,
    query: q,
    cycle: diet?.title ?? "",
    // Named on the face of both exports — see ExportMeta.filter.
    filter: decisionFilter === "all" ? undefined : FILTER_LABEL[decisionFilter],
  };

  const toggle = (list: (string | number)[], v: string | number) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const allPicked = filtered.length > 0 && filtered.every((a) => picked.includes(a.id));

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setBanner(null);
    try {
      const res: any = await fn();
      setBanner({ ok: true, text: res?.message ?? fallback });
      return true;
    } catch (e: any) {
      setBanner({ ok: false, text: e?.data?.message || "That did not work. Please try again." });
      return false;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-4">
        <div className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (isError || !diet) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link
          to={{ pathname: "/admin/upgrade-applications/review-cycles", search: params.toString() }}
          className="text-sm text-gray-500 hover:underline"
        >
          ← Review cycles
        </Link>
        <div className="mt-4 bg-white border border-rose-100 rounded-2xl p-8 text-center">
          <AlertCircle className="w-8 h-8 text-rose-400 mx-auto" />
          <h2 className="mt-3 font-black text-gray-900">Review cycle not available</h2>
          {/* The API answers errors with HTTP 200 and an error body, so the real
              reason arrives in `data.message` rather than as a failed request. */}
          <p className="mt-1 text-sm text-gray-500">
            {String((data as any)?.message || "This review cycle could not be loaded.")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <Link
          to={{ pathname: "/admin/upgrade-applications/review-cycles", search: params.toString() }}
          className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Review cycles
          {filtering ? <span className="text-gray-400">· {grade}</span> : null}
        </Link>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">{diet.title}</h1>
                <span
                  className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                    STATUS_STYLE[diet.status] ?? STATUS_STYLE.pending
                  }`}
                >
                  {diet.status}
                </span>
              </div>
              <p className="text-sm font-semibold text-gray-500">
                {(diet.start_date || "—") + " → " + (diet.end_date || "—")} · {allInDiet.length}{" "}
                application(s) · {panel.length} on the panel
              </p>
              {/* The date this cycle's upgrades take effect from. It lives
                  here, beside the Upgrade button that refuses without it,
                  rather than in the create form — nobody knows it on the day a
                  cycle is opened. */}
              <ConferredOnField diet={diet} />

              {/* The cycle's own figures above are always the whole sitting.
                  This line is the only place the grade filter is allowed to
                  change a number, and it names both. */}
              {filtering ? (
                <p className="mt-1 inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-[11px] font-bold">
                  Showing {inDiet.length} of {allInDiet.length} — {grade} only
                  <Link
                    to={`/admin/upgrade-applications/review-cycles/${id}`}
                    title="Show every grade in this cycle"
                    className="hover:text-indigo-900"
                  >
                    ✕
                  </Link>
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Numbering and the exports are READ-ONLY of the cycle itself —
                one writes to the members register, the others write nothing —
                so they stay available on a closed cycle. A closed sitting is
                precisely when its listing is wanted. */}
            {numbering?.categoryId ? (
              <AccessGate perm={PERMS.upgrade.memberNumberIssue}>
                <button
                  onClick={() => setShowIssueNumbers(true)}
                  title={`Issue ${numbering.grade} numbers to the fully endorsed ${numbering.label} applicants in this cycle`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100"
                >
                  <Hash className="w-3.5 h-3.5" />
                  Issue {numbering.grade} numbers
                  {numbering.awaiting > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-[11px] font-bold">
                      {numbering.awaiting}
                    </span>
                  ) : null}
                </button>
              </AccessGate>
            ) : null}
            {/* Conferring the grade — the step AFTER numbering, and a separate
                button because it is a separate act: numbering puts the new
                number beside the old one, this makes it the number in force and
                the person the new grade. Same privilege, since the two halves
                are done by the same person in the same sitting. */}
            {numbering?.categoryId ? (
              <AccessGate perm={PERMS.upgrade.memberNumberIssue}>
                <button
                  onClick={() => setShowUpgradeGrade(true)}
                  title={upgradeHint}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-bold hover:bg-emerald-100"
                >
                  <GraduationCap className="w-3.5 h-3.5" />
                  Upgrade to {numbering.label}
                  {numbering.upgradable > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold">
                      {numbering.upgradable}
                    </span>
                  ) : null}
                </button>
              </AccessGate>
            ) : null}
            {/* The same two exports the category screen files from, taking the
                rows this screen is showing — grade filter and search included,
                both named in the document. */}
            <button
              onClick={() => exportUpgradesSpreadsheet(filtered, allInDiet, exportMeta)}
              title="Download this cycle's listing as a spreadsheet"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-200 text-emerald-700 text-xs font-bold hover:bg-emerald-50"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              Excel
            </button>
            <button
              onClick={() => exportUpgradesPdf(filtered, allInDiet, exportMeta)}
              title="Print this cycle's listing, or save it as a PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-rose-200 text-rose-700 text-xs font-bold hover:bg-rose-50"
            >
              <Printer className="w-3.5 h-3.5" />
              PDF
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {diet.notes ? <p className="mt-4 text-sm text-gray-500">{diet.notes}</p> : null}

        {/* Said once, at the top, rather than discovered one greyed-out button
            at a time. */}
        {!canAct ? (
          <div className="mt-4 flex items-start gap-2 px-4 py-3 rounded-2xl border border-amber-100 bg-amber-50 text-amber-800 text-sm font-semibold">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {lockedReason} Numbering, the date conferred and the upgrade itself still work, as
              do the listing and the exports below.
            </span>
          </div>
        ) : null}
      </div>

      {banner && (
        <div
          className={`px-4 py-3 rounded-2xl text-sm font-semibold border ${
            banner.ok
              ? "bg-emerald-50 border-emerald-100 text-emerald-800"
              : "bg-rose-50 border-rose-100 text-rose-800"
          }`}
        >
          {banner.text}
        </div>
      )}

      {/* ── Analysis ──────────────────────────────────────────────────── */}
      {analysis ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">Analysis</h2>
          <p className="text-sm text-gray-500 mb-4">
            Where this cycle stands — what it holds, what has been handed out, and what has come back.
            {/* Never narrowed by the grade filter. These figures are what gets
                reported when the sitting closes, and a partial count presented
                as the cycle's position would be wrong. */}
            {filtering ? " Counted across every grade in the cycle, not just " + grade + "." : ""}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="In cycle" value={analysis.applications?.in_cycle} />
            <Stat
              label="Not yet assigned"
              value={analysis.applications?.not_yet_assigned}
              tone={analysis.applications?.not_yet_assigned > 0 ? "text-amber-600" : undefined}
            />
            <Stat
              label="Fully decided"
              value={analysis.applications?.fully_decided}
              tone="text-emerald-600"
            />
            <Stat label="Awaiting decision" value={analysis.applications?.awaiting_decision} />
          </div>

          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Assignments, not recommendations — the total includes the ones
                still sitting with a member, so it is not a count of work done. */}
            <Stat label="Assignments made" value={analysis.reviews?.total} />
            <Stat
              label="Not yet recommended"
              value={analysis.reviews?.pending}
              tone={analysis.reviews?.pending > 0 ? "text-amber-600" : undefined}
            />
            <Stat
              label="Recommended for approval"
              value={analysis.reviews?.approved}
              tone="text-emerald-600"
            />
            <Stat
              label="Recommended for rejection"
              value={analysis.reviews?.rejected}
              tone="text-rose-600"
            />
          </div>

          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Waiting in pool" value={analysis.applications?.waiting_in_pool} />
            <Stat
              label="Held back"
              value={analysis.applications?.held_back}
              tone={analysis.applications?.held_back > 0 ? "text-rose-600" : undefined}
            />
            <Stat
              label="Longest wait"
              value={`${analysis.aging?.longest_days ?? 0}d`}
              tone={(analysis.aging?.longest_days ?? 0) > 30 ? "text-rose-600" : undefined}
            />
            <Stat label="Over 30 days" value={analysis.aging?.over_30_days} />
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {/* Where the cycle's work has got to, counted in APPLICATIONS. The
                review tallies above count assignment rows, so three members on
                one application are three of those and one of these — and "how
                many did we recommend" is only answerable from this one. */}
            <Breakdown title="By decision" data={decisionBreakdown} />
            <Breakdown title="By grade applied for" data={analysis.by_grade} />
            <Breakdown title="By chapter" data={analysis.by_chapter} />
            <Breakdown title="By route" data={analysis.by_route} />
          </div>

          {analysis.workload?.length ? (
            <div className="mt-6">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
                Panel workload in this cycle
              </h3>
              <div className="border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                      <th className="px-4 py-2">Member</th>
                      <th className="px-4 py-2 text-right">Assigned</th>
                      <th className="px-4 py-2 text-right">Decided</th>
                      <th className="px-4 py-2 text-right">Pending</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {analysis.workload.map((w: any) => (
                      <tr key={w.name}>
                        <td className="px-4 py-2 font-semibold text-gray-800">
                          {w.name}
                          {w.is_former ? (
                            <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                              former
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{w.assigned_count}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-emerald-700">
                          {w.decided_count}
                        </td>
                        <td
                          className={`px-4 py-2 text-right tabular-nums font-bold ${
                            w.pending_count > 0 ? "text-amber-600" : "text-gray-400"
                          }`}
                        >
                          {w.pending_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ── What this cycle's recommended applicants pay ──────────────────
          Above the panel deliberately: the levies are what the sitting's
          decisions cost the applicant, and they have to be set before the
          first recommendation opens anybody's bill. */}
      <DietLeviesCard dietId={id} canAct={canAct} lockedReason={lockedReason} />

      {/* Who the cycle has recommended, and who still owes its levies.
          Fed from the rows the screen already holds — `inDiet`, so a grade
          filter narrows this list the same way it narrows the tables below,
          which is what an admin working one grade expects. */}
      <RecommendedApplicantsCard dietId={id} rows={inDiet} />

      {/* ── Panel ─────────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <h2 className="text-lg font-black text-gray-900">Panel</h2>
        <p className="text-sm text-gray-500 mb-4">
          Every Membership Committee member sits on this cycle. The counts are what each is carrying in
          it — open one to see exactly what, and to move work off anybody who has stalled.
        </p>

        {panel.length === 0 ? (
          <div className="text-sm text-gray-400">
            No active Membership Committee was found. Set one up on the active executive set first —{" "}
            <Link to="/executive-sets" className="text-indigo-600 hover:underline">
              Executive Sets
            </Link>
            .
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {panel.map((m: any) => (
              // A card is the natural thing to press to ask "what is this
              // person carrying?" — a button rather than a div so the keyboard
              // reaches it too.
              <button
                key={m.member_id}
                type="button"
                onClick={() => setPanelMember(m)}
                title={`See everything assigned to ${m.name} in this cycle`}
                className={`text-left rounded-2xl border p-4 transition hover:border-indigo-300 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 ${
                  m.is_former ? "border-dashed border-gray-200 bg-gray-50/60" : "border-gray-100"
                }`}
              >
                <div className="font-bold text-gray-900 truncate">{m.name}</div>
                <div className="text-[11px] font-semibold text-gray-400 mt-0.5">
                  {[ROLE_LABEL[m.committee_role] ?? m.committee_role, m.membership_no]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {/* Off the committee now, but their reviews stand. */}
                {m.is_former ? (
                  <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
                    No longer on the committee
                  </div>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                      m.pending_count > 0
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-gray-50 text-gray-400 border-gray-200"
                    }`}
                  >
                    {m.pending_count ?? 0} pending
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border bg-gray-50 text-gray-500 border-gray-200">
                    {m.assigned_count ?? 0} assigned
                  </span>
                </div>
                <div className="mt-2 text-[11px] font-semibold text-indigo-600">
                  See their applications →
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Applications in this cycle ────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-gray-900">
              Applications in this cycle
              {filtering ? <span className="text-gray-400"> · {grade}</span> : null}
            </h2>
            <p className="text-sm text-gray-500">
              {canAct
                ? "Select applications and hand them to a panel member."
                : "What this sitting held, and who decided each one."}
              {filtering
                ? ` Only ${grade} applications are listed — the cycle holds ${allInDiet.length} in total.`
                : ""}
            </p>
            {/* Said before the export buttons are pressed, because the exports
                take WHAT IS ON SCREEN: a roll of 40 taken off a cycle of 175
                with nothing saying which 40 is the kind of thing that gets
                signed and then quoted back as the sitting's whole output. */}
            <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
              Export: {filtered.length} of {inDiet.length} listed
              {decisionFilter === "all" ? "" : ` · ${FILTER_LABEL[decisionFilter]}`}
              {gradeFilter === "all" ? "" : ` · ${gradeFilter}`}
              {q.trim() ? ` · “${q.trim()}”` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Finding four amber rows in a hundred and seventy-five by
                scrolling is not a reasonable ask — this is the shortcut to
                exactly the ones the Upgrade button will pass over. */}
            {blockedCount > 0 ? (
              <button
                onClick={() => setOnlyBlocked((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold ${
                  onlyBlocked
                    ? "border-amber-300 bg-amber-100 text-amber-800"
                    : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                }`}
              >
                <AlertCircle className="w-3.5 h-3.5" />
                {onlyBlocked ? "Showing" : "Show"} {blockedCount} that will be skipped
                {onlyBlocked ? <X className="w-3.5 h-3.5" /> : null}
              </button>
            ) : null}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or membership no…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>
        </div>

        {inDiet.length === 0 ? (
          <p className="mt-6 text-sm text-gray-400">
            {filtering && allInDiet.length > 0
              ? `No ${grade} application is in this cycle — it holds ${allInDiet.length} of other grades.`
              : "Nothing in this cycle yet — pull applications in from the list below."}
          </p>
        ) : (
          <>
            {/* ── Filters ───────────────────────────────────────────────────
                Two labelled rows, not one undifferentiated run of chips, which
                would read as a single filter. The counts partition the cycle —
                see DecisionFilter. */}
            <div className="mt-4 space-y-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-16 shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                  Decision
                </span>
                <Chip
                  label="All"
                  count={inDiet.length}
                  on={decisionFilter === "all"}
                  onClick={() => setDecisionFilter("all")}
                />
                {(
                  [
                    "unassigned",
                    "awaiting",
                    "recommended",
                    "not_recommended",
                    "approved",
                    "rejected",
                    "other",
                  ] as DecisionFilter[]
                )
                  /* Empty slices are dropped, EXCEPT the one that is selected —
                     a chip that vanishes under the press that selected it
                     leaves an empty table with nothing explaining it. "Rejected"
                     and "Other" are usually empty on a live cycle, and a row of
                     zeroes teaches the admin to stop reading the counts. */
                  .filter((key) => (sliceCounts[key] ?? 0) > 0 || decisionFilter === key)
                  .map((key) => (
                    <Chip
                      key={key}
                      label={FILTER_LABEL[key]}
                      count={sliceCounts[key] ?? 0}
                      on={decisionFilter === key}
                      onClick={() => setDecisionFilter(key)}
                      title={
                        key === "unassigned"
                          ? "In the cycle, but nobody has been handed it yet"
                          : key === "awaiting"
                            ? "Handed out, and no committee member has reported yet"
                            : undefined
                      }
                    />
                  ))}
              </div>

              {/* The grade chips are for a cycle being worked whole. Arriving
                  through a grade, the row would be one chip restating the
                  heading, with an "All" beside it reading as though it could
                  widen the page — which it cannot, the server's filter is in
                  the URL. */}
              {!filtering && gradeChips.length > 1 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-16 shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Grade
                  </span>
                  <Chip
                    label="All"
                    count={byDecision.length}
                    on={gradeFilter === "all"}
                    onClick={() => setGradeFilter("all")}
                  />
                  {gradeChips.map(([name, count]) => (
                    <Chip
                      key={name}
                      label={name}
                      count={count}
                      on={gradeFilter === name}
                      onClick={() => setGradeFilter(name)}
                    />
                  ))}
                </div>
              ) : null}

              {chipsOn ? (
                <button
                  type="button"
                  onClick={clearChips}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-400 hover:text-gray-700"
                >
                  <X className="w-3 h-3" /> Clear the filters
                </button>
              ) : null}
            </div>

            {filtered.length === 0 ? (
              <p className="mt-4 text-sm text-gray-400">
                Nothing in this cycle matches what is selected.{" "}
                <button
                  type="button"
                  onClick={() => {
                    clearChips();
                    setQ("");
                    setOnlyBlocked(false);
                  }}
                  className="font-bold text-indigo-600 hover:underline"
                >
                  Clear the filters
                </button>
              </p>
            ) : (
            <div className="mt-4 border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2 w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 disabled:opacity-40"
                        checked={allPicked}
                        disabled={!canAct}
                        title={canAct ? undefined : lockedReason}
                        onChange={() => setPicked(allPicked ? [] : filtered.map((a) => a.id))}
                      />
                    </th>
                    {/* Serial, so a row can be called out by number when the
                        listing is being read aloud or checked against the
                        printed one. It numbers what is ON SCREEN — with a
                        grade filter on, 1..n of that grade. */}
                    <th className="px-4 py-2 w-10 text-right">#</th>
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Upgrade</th>
                    <th className="px-4 py-2">Numbers</th>
                    <th className="px-4 py-2">Reviewers</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((a, i) => (
                    <tr key={a.id} className="hover:bg-gray-50/60 align-top">
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          className="h-4 w-4 disabled:opacity-40"
                          checked={picked.includes(a.id)}
                          disabled={!canAct}
                          title={canAct ? undefined : lockedReason}
                          onChange={() => setPicked((p) => toggle(p, a.id))}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-400 font-semibold">
                        {i + 1}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/admin/upgrade-applications/${a.id}`}
                          className="font-semibold text-gray-900 hover:text-indigo-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {[a.from_grade, a.to_grade].filter(Boolean).join(" → ") || "—"}
                      </td>
                      <td className="px-4 py-2.5">
                        <MemberNumbers row={a} />
                      </td>
                      <td className="px-4 py-2.5">
                        {a.reviews?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {a.reviews.map((r: any) => {
                              const decision = String(r.decision ?? "pending");

                              /* What this chip is already carrying, as a
                                 tooltip: the verdict, who keyed it if it was
                                 not the member, when, and the remark. The chip
                                 itself has room for a name and nothing more,
                                 and an admin about to record a decision for
                                 somebody needs to see whether one is there. */
                              const title = [
                                decision === "pending"
                                  ? canAct
                                    ? `${r.name} has not decided yet — click to record their recommendation`
                                    : `${r.name} has not decided yet`
                                  : `${r.name} ${
                                      decision === "approved" ? "recommends" : "does not recommend"
                                    }`,
                                r.recorded_by_name ? `keyed by ${r.recorded_by_name}` : "",
                                r.decided_at ? String(r.decided_at).slice(0, 16).replace("T", " ") : "",
                                r.remark ? `“${r.remark}”` : "",
                              ]
                                .filter(Boolean)
                                .join(" · ");

                              return (
                                <span
                                  key={r.id}
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                                    DECISION_STYLE[decision] ?? DECISION_STYLE.pending
                                  }`}
                                  title={title}
                                >
                                  {/* The name opens the recording dialog. The
                                      committee's recommendation arrives at the
                                      sitting or on paper as often as it arrives
                                      through the portal, and the secretariat has
                                      to be able to enter it for them. Also on a
                                      decided chip — a mis-keyed verdict has to be
                                      correctable. */}
                                  {canAct ? (
                                    <button
                                      onClick={() => setDeciding({ app: a, review: r })}
                                      className="underline decoration-dotted underline-offset-2 hover:opacity-70"
                                    >
                                      {r.name}
                                    </button>
                                  ) : (
                                    r.name
                                  )}
                                  {/* Keyed by the secretariat, not by the member
                                      themselves. Marked on the row rather than
                                      left to the audit trail: whose
                                      recommendation it is and who typed it are
                                      two different facts. */}
                                  {r.recorded_by_name ? (
                                    <span className="text-[9px] font-black uppercase tracking-wider opacity-60">
                                      desk
                                    </span>
                                  ) : null}
                                  {/* Only an untouched assignment can be moved
                                      or withdrawn — a recorded decision is the
                                      member's own and part of the record.

                                      Both offered, because they are the same
                                      decision taken two ways: this person is
                                      not going to assess it, so either name who
                                      will or put it back for the secretariat to
                                      place again. "I do not know who yet" is the
                                      commoner answer when a reviewer has gone
                                      quiet, which is why the X stays rather
                                      than hiding behind the dialog. */}
                                  {decision === "pending" && canAct && (
                                    <>
                                      <button
                                        onClick={() => setMovingReview({ app: a, review: r })}
                                        title={`Move this application off ${r.name} to another committee member`}
                                        className="rounded hover:text-blue-700"
                                      >
                                        <ArrowRightLeft className="w-3 h-3" />
                                      </button>
                                      <button
                                        onClick={() =>
                                          run(
                                            () => unassign({ id: a.id, reviewId: r.id }).unwrap(),
                                            "Assignment removed.",
                                          )
                                        }
                                        disabled={unassigning}
                                        title="Remove this assignment"
                                        className="rounded hover:text-rose-700 disabled:opacity-50"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </>
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-400">Unassigned</span>
                        )}
                      </td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {/* Decide this one on the committee's behalf. Offered
                            only while the application is still AT the committee
                            stage — past that there is nothing to override, and
                            a button that is always refused teaches the admin to
                            ignore the column. */}
                        {canAct &&
                        ["under_review", "recommended", "not_recommended"].includes(
                          String(a.status ?? "").toLowerCase(),
                        ) ? (
                          <button
                            onClick={() => setOverriding(a)}
                            title="Decide on behalf of the committee (override)"
                            className="mr-1.5 p-1.5 rounded-lg border border-amber-100 text-amber-600 hover:bg-amber-50 hover:border-amber-300"
                          >
                            <ShieldAlert className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => {
                            if (window.confirm(`Take ${a.name || "this application"} out of this cycle?`)) {
                              run(
                                () => removeApp({ id, applicationId: a.id }).unwrap(),
                                "Removed from the cycle.",
                              );
                            }
                          }}
                          disabled={!canAct}
                          title={canAct ? "Remove from this cycle" : lockedReason}
                          className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-rose-600 hover:border-rose-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400 disabled:hover:border-gray-100"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <select
                value={reviewer}
                onChange={(e) => setReviewer(e.target.value)}
                disabled={!canAct}
                title={canAct ? undefined : lockedReason}
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <option value="">Assign to…</option>
                {/* Former members stay visible in the panel and workload, but
                    no new work can be handed to them. */}
                {panel
                  .filter((m: any) => !m.is_former)
                  .map((m: any) => (
                    <option key={m.member_id} value={m.member_id}>
                      {m.name} ({m.pending_count ?? 0} pending)
                    </option>
                  ))}
              </select>
              <button
                onClick={async () => {
                  const done = await run(
                    () => assignBulk({ member_id: reviewer, application_ids: picked }).unwrap(),
                    "Assigned.",
                  );
                  if (done) setPicked([]);
                }}
                disabled={!canAct || assigning || !reviewer || picked.length === 0}
                title={canAct ? undefined : lockedReason}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
              >
                {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Assign {picked.length || ""}
              </button>
            </div>

            {!canAct && (
              <p className="mt-2 text-right text-[11px] font-semibold text-amber-600">
                {lockedReason}
              </p>
            )}
            {canAct && panel.length === 0 && (
              <p className="mt-2 text-right text-[11px] font-semibold text-amber-600">
                There is no active Membership Committee to assign to.
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Pull applications in ──────────────────────────────────────────
          Not shown at all on a cycle that is not sitting. The pool is a list of
          work waiting to be taken, and offering it beside a button that cannot
          be pressed invites the admin to pick a batch and then be refused. */}
      {!canAct ? null : (
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <h2 className="text-lg font-black text-gray-900">
          Waiting to be added
          {filtering ? <span className="text-gray-400"> · {grade}</span> : null}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Applications at committee review, in no cycle, with the fee settled, the chapter endorsement
          in and both referees approved — the ones a panel member can actually be given.
          {filtering
            ? ` Only ${grade} applications are listed — ${allUnassigned.length} are waiting in total.`
            : ""}
        </p>

        {unassignedPool.length === 0 ? (
          <p className="text-sm text-gray-400">
            {filtering && allUnassigned.length > 0
              ? `No ${grade} application is waiting — the ${allUnassigned.length} in the pool are of other grades.`
              : "Nothing waiting — every application at committee review is already in a cycle."}
          </p>
        ) : (
          <>
            <div className="border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2 w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={
                          unassignedPool.length > 0 &&
                          unassignedPool.every((a) => pulled.includes(a.id))
                        }
                        onChange={() =>
                          setPulled(
                            unassignedPool.every((a) => pulled.includes(a.id))
                              ? []
                              : unassignedPool.map((a) => a.id),
                          )
                        }
                      />
                    </th>
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Upgrade</th>
                    <th className="px-4 py-2">Route</th>
                    <th className="px-4 py-2 text-right">Waiting</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {unassignedPool.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => openApplication(a.id)}
                      title="Open this application"
                      className="hover:bg-gray-50/60 cursor-pointer"
                    >
                      {/* The checkbox is for picking, not opening — the row
                          click must not fire underneath it, or choosing a batch
                          would navigate away from the batch. */}
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={pulled.includes(a.id)}
                          onChange={() => setPulled((p) => toggle(p, a.id))}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <Link
                          to={`/admin/upgrade-applications/${a.id}`}
                          className="font-semibold text-gray-900 hover:text-indigo-600"
                        >
                          {a.name || "—"}
                        </Link>
                        <div className="text-[11px] text-gray-400">{a.membership_no || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {[a.from_grade, a.to_grade].filter(Boolean).join(" → ") || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-gray-500">{a.route || "—"}</td>
                      <td
                        className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                          (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                        }`}
                      >
                        {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={async () => {
                  const done = await run(
                    () => addApps({ id, application_ids: pulled }).unwrap(),
                    "Added to the cycle.",
                  );
                  if (done) setPulled([]);
                }}
                disabled={adding || pulled.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-50"
              >
                {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add {pulled.length || ""} to cycle
              </button>
            </div>
          </>
        )}
      </div>
      )}

      {/* ── Held back ─────────────────────────────────────────────────────
          Part of the same picture as the pool — what would be addable once its
          blockers clear — so it goes with it on a cycle that is not sitting. */}
      {canAct && notReady.length > 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-gray-900">
            Held back
            {filtering ? <span className="text-gray-400"> · {grade}</span> : null}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            At committee review but not yet assignable. Each one names what is missing — clearing it
            moves the application into the pool above.
            {filtering
              ? ` Only ${grade} applications are listed — ${allNotReady.length} are held back in total.`
              : ""}
          </p>

          <div className="border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                  <th className="px-4 py-2">Applicant</th>
                  <th className="px-4 py-2">Upgrade</th>
                  <th className="px-4 py-2">Waiting on</th>
                  <th className="px-4 py-2 text-right">Waiting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {notReady.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => openApplication(a.id)}
                    title="Open this application"
                    className="hover:bg-gray-50/60 cursor-pointer"
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/upgrade-applications/${a.id}`}
                        className="font-semibold text-gray-900 hover:text-indigo-600"
                      >
                        {a.name || "—"}
                      </Link>
                      <div className="text-[11px] text-gray-400">{a.membership_no || "—"}</div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {[a.from_grade, a.to_grade].filter(Boolean).join(" → ") || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1.5">
                        {(a.blockers ?? []).map((b: string) => (
                          <span
                            key={b}
                            className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200"
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right tabular-nums font-bold ${
                        (a.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                      }`}
                    >
                      {a.waiting_days != null ? `${a.waiting_days}d` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Mounted only while open, so the numbering preview is fetched on demand
          rather than on every visit to a cycle. */}
      {showIssueNumbers && numbering?.categoryId ? (
        <IssueMemberNumbersModal
          categoryId={numbering.categoryId}
          categoryName={numbering.label}
          dietId={id}
          dietTitle={diet.title}
          onClose={() => {
            setShowIssueNumbers(false);
            refetch();
          }}
        />
      ) : null}

      {/* Likewise on demand — and refetched on close, because the grades and
          numbers this writes are what the table above is showing. */}
      {showUpgradeGrade && numbering?.categoryId ? (
        <ApplyGradeUpgradeModal
          categoryId={numbering.categoryId}
          categoryName={numbering.label}
          dietId={id}
          dietTitle={diet.title}
          onClose={() => {
            setShowUpgradeGrade(false);
            refetch();
          }}
        />
      ) : null}

      {/* Recording a committee member's recommendation for them. The banner
          carries the server's own sentence — it is the one that says whether
          this decision moved the application, which the table cannot show on
          its own. */}
      {deciding ? (
        <RecordCommitteeDecisionModal
          applicationId={deciding.app.id}
          applicantName={deciding.app.name}
          review={deciding.review}
          onClose={() => setDeciding(null)}
          onDone={(message) => {
            setBanner({ ok: true, text: message });
            refetch();
          }}
        />
      ) : null}

      {/* The override. Same banner treatment — the server's sentence is the one
          that says what the decision did to the applicant's induction bill. */}
      {overriding ? (
        <OverrideCommitteeDecisionModal
          app={overriding}
          onClose={() => setOverriding(null)}
          onDone={(message) => {
            setBanner({ ok: true, text: message });
            refetch();
          }}
        />
      ) : null}

      {/* What one panel member is carrying in this cycle. A lens over the rows
          the page already holds, so it and the reviewer chips cannot disagree
          about who has what. */}
      {panelMember ? (
        <CommitteePanelMemberModal
          member={panelMember}
          rows={inDiet}
          gradeNote={filtering ? `${grade} only` : null}
          canAct={canAct}
          busy={unassigning}
          onMove={(held) => {
            // Close the lens first: two stacked dialogs over one backdrop makes
            // a single press close both.
            setPanelMember(null);
            setMovingReview(held);
          }}
          onWithdraw={(held) =>
            run(
              () => unassign({ id: held.app.id, reviewId: held.review.id }).unwrap(),
              "Assignment withdrawn.",
            )
          }
          onClose={() => setPanelMember(null)}
        />
      ) : null}

      {/* Moving a pending assignment to somebody else. The banner carries the
          server's own sentence, which names who it went from and to. */}
      {movingReview ? (
        <ReassignCommitteeReviewModal
          applicationId={movingReview.app.id}
          applicantName={movingReview.app.name}
          review={movingReview.review}
          panel={panel}
          onClose={() => setMovingReview(null)}
          onDone={(message) => {
            setBanner({ ok: true, text: message });
            refetch();
          }}
        />
      ) : null}
    </div>
  );
}
